/**
 * combat/ability_engine.ts — The Fire Behind the Button
 *
 * Pressing 1-6 on the skill bar used to be a hollow ritual: EP vanished,
 * cooldowns spun, but nothing in the world actually changed. This module
 * gives every ability its teeth. Damage lands. Heals mend. Buffs wrap
 * you in light. The engine reads the AbilityDefinition, walks its effects
 * array, and makes each one real — crit rolls, variance, screen shake,
 * floating damage numbers, the whole visceral chain.
 *
 * Called from loop.ts after EP/cooldown checks pass. One function, one
 * ability, one moment of consequence.
 */

import type { GameState, DamageNumber, DamageType, BuffType } from "../engine/state.js";
import { getAbility, getWeaponAbilities, type AbilityDefinition, type AbilityEffect } from "../combat/skills.js";
import { applyBuff } from "../combat/buffs.js";
import { getCreatureHp } from "../combat/system.js";

// ─── Constants ───────────────────────────────────────────────────────────────

const CRIT_CHANCE = 0.10;
const CRIT_MULTIPLIER = 1.5;
const DAMAGE_VARIANCE = 0.20;         // ±20% roll
const EXECUTE_HP_THRESHOLD = 0.30;    // 30% HP threshold for execute bonus
const EXECUTE_BONUS = 0.50;           // 50% bonus damage below threshold
const LIFESTEAL_FRACTION = 0.30;      // 30% of damage healed back
const MAX_TARGET_RANGE = 2;           // tiles — fallback target scan radius

// ─── Damage Number Helpers ───────────────────────────────────────────────────

function pushCombatDamageNumber(
  state: GameState,
  value: number,
  position: { tileX: number; tileY: number },
  isPlayerDamage: boolean,
  isCrit: boolean,
  damageType: DamageType,
): void {
  const screenX = (position.tileX - position.tileY) * 32 + (Math.random() - 0.5) * 16;
  const screenY = (position.tileX + position.tileY) * 16 - 40 + (Math.random() - 0.5) * 8;

  state.player.combatState.damageNumbers.push({
    value,
    x: screenX,
    y: screenY,
    createdAt: performance.now() / 1000,
    isPlayerDamage,
    isCrit,
    damageType,
    angle: -Math.PI / 2 + (Math.random() - 0.5) * 0.6,
  });
}

function pushHealNumber(
  state: GameState,
  value: number,
): void {
  const pos = state.player.position;
  const screenX = (pos.tileX - pos.tileY) * 32 + (Math.random() - 0.5) * 16;
  const screenY = (pos.tileX + pos.tileY) * 16 - 40 + (Math.random() - 0.5) * 8;

  state.player.combatState.damageNumbers.push({
    value,
    x: screenX,
    y: screenY,
    createdAt: performance.now() / 1000,
    isPlayerDamage: false,
    isCrit: false,
    damageType: "heal",
    angle: -Math.PI / 2 + (Math.random() - 0.5) * 0.3, // tighter upward float for heals
  });
}

// ─── Tile Distance ───────────────────────────────────────────────────────────

function tileDistance(
  a: { tileX: number; tileY: number },
  b: { tileX: number; tileY: number },
): number {
  const dx = a.tileX - b.tileX;
  const dy = a.tileY - b.tileY;
  return Math.sqrt(dx * dx + dy * dy);
}

// ─── Resolve Combat Target ──────────────────────────────────────────────────

/**
 * Find the creature being targeted. Uses the explicit combat target if set,
 * otherwise falls back to the nearest creature within range.
 */
function resolveTarget(state: GameState): { creatureId: string; creaturePos: { tileX: number; tileY: number }; hpRef: { hp: number; maxHp: number } } | null {
  const cs = state.player.combatState;

  // Primary: explicit combat target
  if (cs.active && cs.targetId) {
    const creature = state.creatures.find(c => c.id === cs.targetId);
    const hpRef = getCreatureHp(cs.targetId);
    if (creature && hpRef) {
      return { creatureId: cs.targetId, creaturePos: creature.position, hpRef };
    }
  }

  // Fallback: nearest creature within MAX_TARGET_RANGE tiles
  let nearest: { creatureId: string; creaturePos: { tileX: number; tileY: number }; hpRef: { hp: number; maxHp: number } } | null = null;
  let nearestDist = Infinity;

  for (const creature of state.creatures) {
    const dist = tileDistance(state.player.position, creature.position);
    if (dist <= MAX_TARGET_RANGE && dist < nearestDist) {
      const hpRef = getCreatureHp(creature.id);
      if (hpRef && hpRef.hp > 0) {
        nearest = { creatureId: creature.id, creaturePos: creature.position, hpRef };
        nearestDist = dist;
      }
    }
  }

  return nearest;
}

// ─── Core Damage Calculation ─────────────────────────────────────────────────

function calcAbilityDamage(
  baseValue: number,
  scaling: number,
  playerAttack: number,
): { damage: number; isCrit: boolean } {
  const raw = baseValue + scaling * playerAttack;

  // ±20% variance roll
  const roll = 1 + (Math.random() * 2 - 1) * DAMAGE_VARIANCE;
  let damage = Math.max(1, Math.round(raw * roll));

  // Crit check
  const isCrit = Math.random() < CRIT_CHANCE;
  if (isCrit) {
    damage = Math.round(damage * CRIT_MULTIPLIER);
  }

  return { damage, isCrit };
}

// ─── Apply Screen Shake ──────────────────────────────────────────────────────

function applyShake(state: GameState, intensity: number): void {
  if (intensity <= 0) return;
  state.screenShake.intensity = Math.min(12, state.screenShake.intensity + intensity);
  state.screenShake.decay = 10;
  state.screenShake.phase = 0;
}

// ─── Deal Damage to Target ───────────────────────────────────────────────────

function dealDamageToTarget(
  state: GameState,
  damage: number,
  isCrit: boolean,
  damageType: DamageType,
  target: { creatureId: string; creaturePos: { tileX: number; tileY: number }; hpRef: { hp: number; maxHp: number } },
): void {
  target.hpRef.hp -= damage;

  // Record hit time for creature flash effect
  state.creatureHitTimes.set(target.creatureId, performance.now() / 1000);

  // Floating damage number
  pushCombatDamageNumber(state, damage, target.creaturePos, false, isCrit, damageType);
}

// ═══════════════════════════════════════════════════════════════════════════
//  EXECUTE ABILITY — The main event
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Execute a fully resolved ability. Called from loop.ts after EP deduction
 * and cooldown/GCD checks have already passed. This function walks the
 * ability's effects array and makes each one real in the game world.
 */
export function executeAbility(state: GameState, abilityId: string): void {
  const def = getAbility(abilityId);
  if (!def) return;

  const playerAttack = state.player.combat.attack;

  for (const effect of def.effects) {
    switch (effect.type) {
      case "damage":
        processDirectDamage(state, effect, playerAttack, def);
        break;

      case "heal":
        processHeal(state, effect, playerAttack, abilityId);
        break;

      case "buff":
        processBuff(state, effect, abilityId);
        break;

      case "debuff":
        processDebuff(state, effect, abilityId);
        break;

      case "execute":
        processExecute(state, effect, playerAttack, def);
        break;

      case "lifesteal":
        processLifesteal(state, effect, playerAttack, def);
        break;

      case "knockback":
        processKnockback(state, def);
        break;
    }
  }
}

// ─── Effect Processors ───────────────────────────────────────────────────────

function processDirectDamage(
  state: GameState,
  effect: AbilityEffect,
  playerAttack: number,
  def: AbilityDefinition,
): void {
  const target = resolveTarget(state);
  if (!target) return;

  const { damage, isCrit } = calcAbilityDamage(effect.baseValue, effect.scaling, playerAttack);
  dealDamageToTarget(state, damage, isCrit, effect.damageType ?? "physical", target);
  applyShake(state, def.visual.shakeIntensity);
}

function processHeal(
  state: GameState,
  effect: AbilityEffect,
  playerAttack: number,
  abilityId: string,
): void {
  if (effect.duration > 0) {
    // HoT — apply as a regen buff that ticks over time
    applyBuff(
      state,
      "regen",
      Math.round(effect.baseValue + effect.scaling * playerAttack),
      effect.duration,
      effect.tickInterval || 1,
      effect.maxStacks,
      abilityId,
    );
  } else {
    // Instant heal
    const healAmount = Math.max(1, Math.round(effect.baseValue + effect.scaling * playerAttack));
    state.player.combat.hp = Math.min(
      state.player.combat.hp + healAmount,
      state.player.combat.maxHp,
    );
    pushHealNumber(state, healAmount);
  }
}

function processBuff(
  state: GameState,
  effect: AbilityEffect,
  abilityId: string,
): void {
  if (!effect.buffType) return;
  applyBuff(
    state,
    effect.buffType,
    effect.baseValue,
    effect.duration,
    effect.tickInterval,
    effect.maxStacks,
    abilityId,
  );
}

function processDebuff(
  state: GameState,
  effect: AbilityEffect,
  abilityId: string,
): void {
  if (!effect.buffType) return;

  // NOTE: We don't have creature buff tracking yet. Enemy-targeting debuffs
  // (stun, slow, poison, etc.) are applied as player buffs for now, which
  // is only correct for self-targeting debuffs. When creature buff tracking
  // is implemented, enemy-targeting debuffs should be routed to the target
  // creature's buff list instead.
  applyBuff(
    state,
    effect.buffType,
    effect.baseValue,
    effect.duration,
    effect.tickInterval,
    effect.maxStacks,
    abilityId,
  );
}

function processExecute(
  state: GameState,
  effect: AbilityEffect,
  playerAttack: number,
  def: AbilityDefinition,
): void {
  const target = resolveTarget(state);
  if (!target) return;

  let { damage, isCrit } = calcAbilityDamage(effect.baseValue, effect.scaling, playerAttack);

  // Execute bonus: 50% extra damage if target is below 30% HP
  const hpFraction = target.hpRef.hp / target.hpRef.maxHp;
  if (hpFraction < EXECUTE_HP_THRESHOLD) {
    damage = Math.round(damage * (1 + EXECUTE_BONUS));
  }

  dealDamageToTarget(state, damage, isCrit, effect.damageType ?? "physical", target);
  applyShake(state, def.visual.shakeIntensity);
}

function processLifesteal(
  state: GameState,
  effect: AbilityEffect,
  playerAttack: number,
  def: AbilityDefinition,
): void {
  const target = resolveTarget(state);
  if (!target) return;

  const { damage, isCrit } = calcAbilityDamage(effect.baseValue, effect.scaling, playerAttack);
  dealDamageToTarget(state, damage, isCrit, effect.damageType ?? "shadow", target);

  // Heal for a percentage of the damage dealt
  const healAmount = Math.max(1, Math.round(damage * LIFESTEAL_FRACTION));
  state.player.combat.hp = Math.min(
    state.player.combat.hp + healAmount,
    state.player.combat.maxHp,
  );
  pushHealNumber(state, healAmount);
  applyShake(state, def.visual.shakeIntensity);
}

function processKnockback(
  state: GameState,
  def: AbilityDefinition,
): void {
  // TODO: Implement actual tile displacement for knockback.
  // For now, apply extra screen shake to sell the impact.
  applyShake(state, Math.max(def.visual.shakeIntensity, 5));
}

// ═══════════════════════════════════════════════════════════════════════════
//  STARTER ABILITIES — Equip tier-0 abilities on character creation
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Populate the skill bar with the three starter abilities — one from each
 * Ember tree. Called when character creation finishes so the player isn't
 * staring at an empty bar when they first encounter hostiles.
 */
export function assignStarterAbilities(state: GameState): void {
  const starters: Array<{ slot: number; id: string }> = [
    { slot: 0, id: "flame_strike" },
    { slot: 1, id: "bark_shield" },
    { slot: 2, id: "gust_slash" },
  ];

  for (const { slot, id } of starters) {
    const def = getAbility(id);
    if (def) {
      state.player.abilityBar.slots[slot] = {
        id,
        cooldownEnd: 0,
        charges: def.maxCharges,
      };
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════════
//  WEAPON ABILITIES — Sync skill bar slots 3-5 with equipped weapon
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Read the equipped weapon, resolve its class, and populate skill bar
 * slots 3-5 with weapon-granted abilities. Clears those slots if no
 * weapon is equipped or the weapon class has no abilities.
 */
export function updateWeaponAbilities(state: GameState): void {
  const weapon = state.player.equipment.weapon;

  // Clear weapon slots first
  for (let i = 3; i <= 5; i++) {
    state.player.abilityBar.slots[i] = null;
  }

  if (!weapon?.equipmentData?.weaponVisual) return;

  const weaponClass = weapon.equipmentData.weaponVisual.class;
  const abilities = getWeaponAbilities(weaponClass);

  for (let i = 0; i < abilities.length && i < 3; i++) {
    const def = abilities[i];
    state.player.abilityBar.slots[3 + i] = {
      id: def.id,
      cooldownEnd: 0,
      charges: def.maxCharges,
    };
  }
}
